<script setup lang="ts">
import { computed, markRaw } from 'vue';
import { BFormFile } from 'bootstrap-vue-next';
import { required as requiredRule } from '@regle/rules';
import { useItpFormControl, useItpFormField } from './useItpForm';

defineOptions({ inheritAttrs: false });

const props = defineProps<{
  modelValue?: File | File[] | null;
  value?: File | File[] | null;
  name: string;
  id?: string;
  placeholder?: string;
  disabled?: boolean;
  readonly?: boolean;
  required?: boolean;
  size?: 'sm' | 'md' | 'lg';

  accept?: string;
  multiple?: boolean;
  browseText?: string;
  dropPlaceholder?: string;
  directory?: boolean;
  noTraverse?: boolean;
  noDrop?: boolean;
  plain?: boolean;
  lang?: string;
  validation?: any;
  autofocus?: boolean | string;
}>();

const emit = defineEmits<{
  (event: 'update:modelValue', value: File | File[] | null): void;
  (event: 'change', value: File | File[] | null): void;
  (event: 'input', value: File | File[] | null): void;
  (event: 'blur'): void;
}>();

const { isDisabled, isReadonly, isReadonlyOrDisabled } =
  useItpFormControl(props);

const { form, touch } = useItpFormField(props, (isRequired) =>
  isRequired ? { required: requiredRule } : {},
);

function safeMark(value: File | File[] | null): File | File[] | null {
  if (value instanceof File) {
    return markRaw(value);
  }
  if (Array.isArray(value)) {
    return value.map((f) => (f instanceof File ? markRaw(f) : f));
  }
  return value;
}

const fileValue = computed({
  get() {
    return props.modelValue ?? null;
  },
  set(newValue) {
    const safe = safeMark(newValue);

    if (form) {
      form.updateFieldValue(props.name, safe);
    }

    emit('update:modelValue', safe);
    emit('change', safe);
    emit('input', safe);
    touch();
  },
});
</script>

<template>
  <BFormFile
    :id="id || name"
    v-model="fileValue"
    :name="name"
    :accept="accept"
    :multiple="multiple"
    :disabled="isReadonlyOrDisabled"
    :size="size"
    :placeholder="placeholder"
    :browse-text="browseText"
    :drop-placeholder="dropPlaceholder"
    :directory="directory"
    :no-traverse="noTraverse"
    :no-drop="noDrop"
    :plain="plain"
    @blur="$emit('blur')"
  />
</template>

